import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Printer,
  ShieldCheck,
  Warning,
  QrCode,
  Camera,
  Receipt,
  Storefront,
  Lightning,
  WhatsappLogo,
  ArrowRight,
  CreditCard,
  Sticker,
  Sparkle,
  DeviceMobile,
  ClockClockwise,
  Lock,
} from "@phosphor-icons/react";

/* ─────────────────────────────────────────────────────────────────────────
   Curb Checkpoint × Postnet — channel-partner pitch deck (v2).
   Route: /pitch/postnet
   Audience: independent Postnet franchise owners.

   v2 model (Feb 2026):
   - The customer carries one CARD with a private document vault.
   - Postnet sells 3 SKUs at the counter: Curb Card, Curb Asset Stickers,
     Curb Mint Stickers (the document-binding stickers used by clerks).
   - Verification is phone-to-phone — no paper, no app, no install.
   - Documents expire every 90 days → recurring foot-traffic.
   - Honest framing: any Postnet refreshes; convenience wins, not lock-in.
   ───────────────────────────────────────────────────────────────────────── */

const POSTNET_RED = "#E30613";
const MINT = "#10B981";
const CURB_YELLOW = "#FFCC00";

const Slide = ({ children, bg = "bg-white", className = "" }) => (
  <section
    className={`postnet-slide ${bg} ${className} relative w-full min-h-[100vh] sm:min-h-[820px] flex flex-col`}
  >
    {children}
  </section>
);

const SlideNum = ({ n, total = 6, dark = false }) => (
  <div
    className={`absolute top-6 right-6 sm:top-10 sm:right-10 font-mono text-[11px] tracking-[0.3em] uppercase ${
      dark ? "text-white/60" : "text-neutral-400"
    }`}
  >
    {String(n).padStart(2, "0")} / {String(total).padStart(2, "0")}
  </div>
);

const Eyebrow = ({ children, color = "text-neutral-500" }) => (
  <div className={`font-mono text-[10px] tracking-[0.4em] uppercase ${color}`}>
    {children}
  </div>
);

const StatCard = ({ value, label, sub, accent }) => (
  <div className="bg-white border-2 border-black p-5">
    <div
      className="font-display text-3xl sm:text-4xl leading-none"
      style={accent ? { color: accent } : {}}
    >
      {value}
    </div>
    <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-3 text-neutral-700">
      {label}
    </div>
    {sub && (
      <div className="text-xs text-neutral-600 mt-2 leading-snug">{sub}</div>
    )}
  </div>
);

const SkuCard = ({ icon: Icon, color, name, tag, who, wholesale, blurb }) => (
  <div className="bg-white border-2 border-black p-6 flex flex-col">
    <div
      className="w-10 h-10 flex items-center justify-center text-white"
      style={{ background: color }}
    >
      <Icon size={20} weight="bold" />
    </div>
    <div className="font-mono text-[10px] tracking-[0.3em] uppercase mt-4 text-neutral-500">
      {tag}
    </div>
    <div className="font-display text-2xl sm:text-3xl mt-1 leading-tight">
      {name}
    </div>
    <div className="text-xs text-neutral-600 mt-3 leading-relaxed flex-1">
      {blurb}
    </div>
    <div className="border-t border-neutral-200 mt-5 pt-4 grid grid-cols-2 gap-2 text-xs">
      <div>
        <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-400">
          WHO USES IT
        </div>
        <div className="font-bold mt-1">{who}</div>
      </div>
      <div>
        <div className="font-mono text-[9px] tracking-widest uppercase text-neutral-400">
          WHOLESALE
        </div>
        <div className="font-bold mt-1" style={{ color: POSTNET_RED }}>
          {wholesale}
        </div>
      </div>
    </div>
  </div>
);

export default function PitchPostnet() {
  return (
    <div className="bg-white">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .postnet-slide { min-height: auto !important; page-break-after: always; }
        }
      `}</style>

      {/* Sticky toolbar — hidden in print */}
      <div className="no-print sticky top-0 bg-black text-white border-b border-black z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            to="/cc/enterprise"
            className="font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2"
            data-testid="postnet-pitch-back"
            style={{ color: POSTNET_RED }}
          >
            <ArrowLeft size={14} weight="bold" /> Back
          </Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase hidden sm:inline-flex items-center gap-2">
            <ShieldCheck size={14} weight="bold" /> CURB CHECKPOINT × POSTNET
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://wa.me/?text=Take%20a%20look%20at%20this%20Postnet%20%C3%97%20Curb%20Checkpoint%20pilot%3A%20https%3A%2F%2Fcurbcrew.co.za%2Fpitch%2Fpostnet"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2 bg-[#25D366] text-white px-3 py-1.5"
              data-testid="postnet-pitch-share"
            >
              <WhatsappLogo size={14} weight="bold" /> Share
            </a>
            <button
              onClick={() => window.print()}
              className="font-mono text-[10px] tracking-[0.3em] uppercase inline-flex items-center gap-2 text-white px-3 py-1.5"
              style={{ background: POSTNET_RED }}
              data-testid="postnet-pitch-print"
            >
              <Printer size={14} weight="bold" /> Print / PDF
            </button>
          </div>
        </div>
      </div>

      {/* ───────── SLIDE 01 · COVER ───────── */}
      <Slide bg="bg-black text-white" className="justify-center">
        <SlideNum n={1} dark />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <div className="flex items-center gap-3">
            <span
              className="inline-block w-3 h-3"
              style={{ background: POSTNET_RED }}
            />
            <Eyebrow color="text-white/60">
              CHANNEL PARTNER PROPOSAL · 2026
            </Eyebrow>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] mt-6">
            Three Curb Crew SKUs.<br />
            <span
              className="px-2 inline-block"
              style={{ background: POSTNET_RED }}
            >
              R49 each. Wholesale.
            </span>
          </h1>
          <p className="mt-10 text-base sm:text-xl max-w-2xl text-white/80 leading-relaxed">
            A counter stand at your Postnet. Three Curb Crew products
            supplied at a flat R49 wholesale. Your branch sets retail
            pricing. Your branch keeps the margin. We supply the stock,
            the dashboard, the verification engine, and the training.
          </p>

          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            <StatCard
              value="R49"
              label="WHOLESALE · PER CARD"
              sub="Curb Crew card. You set retail."
              accent={POSTNET_RED}
            />
            <StatCard
              value="R49"
              label="WHOLESALE · ASSET STICKER PACK"
              sub="10-pack QR stickers. You set retail."
              accent={POSTNET_RED}
            />
            <StatCard
              value="R49"
              label="WHOLESALE · MINT STICKER PACK"
              sub="Operator-only stock. You set retail."
              accent={POSTNET_RED}
            />
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 02 · PROBLEM ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={2} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <div className="flex items-center gap-3">
            <Warning size={20} weight="bold" style={{ color: POSTNET_RED }} />
            <Eyebrow>THE PROBLEM</Eyebrow>
          </div>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight mt-4">
            Document services for drivers{" "}
            <span
              className="px-1 text-white inline-block"
              style={{ background: POSTNET_RED }}
            >
              are a one-off transaction.
            </span>
          </h2>

          <div className="grid sm:grid-cols-2 gap-6 mt-12">
            <div className="bg-neutral-50 border-2 border-black p-6">
              <Eyebrow>WHAT POSTNET HANDLES TODAY</Eyebrow>
              <p className="mt-3 text-sm leading-relaxed text-neutral-800">
                Police clearance applications, e-hailing driver document
                services, PrDP renewals, ID and licence support. A driver
                walks in, gets the document handled, walks out. There&apos;s
                no reason for them to come back to <strong>your</strong>{" "}
                branch instead of the one down the road. A transaction.
                Not a relationship.
              </p>
            </div>
            <div className="bg-neutral-50 border-2 border-black p-6">
              <Eyebrow>WHAT&apos;S BREAKING DOWNSTREAM</Eyebrow>
              <p className="mt-3 text-sm leading-relaxed text-neutral-800">
                Uber, Bolt, fleet operators, employers — they&apos;re
                increasingly rejecting paper documents as suspect.
                There&apos;s no way to prove that a document signed off by
                <em> your branch</em> is the same one that landed in front
                of a regulator weeks later. Drivers lose work. You lose
                repeat visits.
              </p>
            </div>
          </div>

          <div
            className="mt-12 border-2 border-black p-6 text-white"
            style={{ background: POSTNET_RED }}
          >
            <Eyebrow color="text-white/70">THE GAP</Eyebrow>
            <h3 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">
              Postnet has no loyalty programme, no recurring product, and no
              digital trust layer. Curb Crew supplies all three — at one
              counter — in one box.
            </h3>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 03 · THE SHELF · 3 PRODUCTS ───────── */}
      <Slide
        bg="bg-[color:var(--cc-yellow)] text-black"
        className="border-t-2 border-black"
      >
        <SlideNum n={3} />
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>THE SHELF · 2 RETAIL SKUS + 1 OPERATOR TOOL</Eyebrow>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight mt-4">
            Two products on the shelf.<br />
            <span className="underline decoration-4">One tool behind the counter.</span>
          </h2>

          <div className="grid sm:grid-cols-3 gap-5 mt-12">
            <SkuCard
              icon={CreditCard}
              color="#000000"
              tag="RETAIL · ON THE SHELF"
              name="Curb Crew Card"
              who="Customer carries"
              wholesale="R49"
              blurb="The PVC card the customer carries in their wallet. Holds a private vault of certified documents. Works like a bank card — except for paperwork. Branded Postnet × Curb Crew."
            />
            <SkuCard
              icon={Sticker}
              color={POSTNET_RED}
              tag="RETAIL · ON THE SHELF"
              name="Curb Asset Stickers"
              who="Cardholder"
              wholesale="R49 / pack"
              blurb="QR stickers for what the customer owns — geyser, aircon, tools, products on a shelf. Stick once, route customers back to them forever."
            />
            <SkuCard
              icon={Sparkle}
              color={MINT}
              tag="OPERATOR ONLY · BEHIND COUNTER"
              name="Curb Mint Stickers"
              who="Clerk only"
              wholesale="R49 / pack"
              blurb="Never sold to the public. Lives in a drawer behind the counter. The clerk peels one, scans it into the portal, binds it to a customer's vault. A sticker alone is worthless — only a certified clerk's action gives it trust."
            />
          </div>

          <div className="mt-10 bg-white border-2 border-black p-6">
            <Eyebrow>WHAT YOUR BRANCH GETS FROM CURB CREW</Eyebrow>
            <ul className="mt-3 space-y-2 text-sm leading-relaxed">
              <li>
                <strong>Stock</strong> · Three SKUs delivered to your branch
                at R49 wholesale. Restock on demand, no upfront commitment.
              </li>
              <li>
                <strong>Counter stand</strong> · Branded Postnet × Curb Crew
                A4 stand supplied free for the pilot.
              </li>
              <li>
                <strong>Operator portal</strong> · Branded clerk dashboard at{" "}
                <code className="font-mono bg-neutral-100 px-1.5 py-0.5 text-xs">
                  /cc/postnet
                </code>{" "}
                — no install, runs on your existing counter laptop.
              </li>
              <li>
                <strong>Training</strong> · 15-minute walkthrough for your
                clerks. Printed cheat-sheet behind the counter.
              </li>
              <li>
                <strong>Verification engine</strong> · The cryptographic
                back-end that makes every Curb Crew card unforgeable. Always
                live, always free.
              </li>
            </ul>
            <p className="mt-5 text-xs text-neutral-600">
              <strong>Retail pricing is yours.</strong> What you charge the
              customer, your turnover, your royalty reporting — that&apos;s
              your business. Curb Crew sells you stock at R49 wholesale.
              Full stop.
            </p>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 04 · HOW IT WORKS · COUNTER + PHONE ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={4} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>HOW IT WORKS · ONE COUNTER · ZERO ADMIN FOR POSTNET</Eyebrow>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight mt-4">
            The vault opens on a phone.<br />
            <span style={{ color: POSTNET_RED }}>Anyone</span> can verify it.
          </h2>

          <div className="mt-12 space-y-5">
            <Step n="01" title="Walk-in" icon={Storefront}>
              Customer comes in for a document service — police clearance,
              PrDP renewal, ID support. Clerk asks:{" "}
              <em>&quot;Want to keep this in your private vault for next
              time?&quot;</em>{" "}
              If yes, and they don&apos;t already have a Curb Crew card, they
              pick one off the stand.
            </Step>

            <Step n="02" title="Bind the document" icon={Sparkle} color={MINT}>
              Clerk peels a <strong>Curb Mint Sticker</strong> from the
              roll. Scans its code into the operator portal at{" "}
              <code className="font-mono bg-neutral-100 px-1.5 py-0.5 text-xs">
                /cc/postnet
              </code>
              . Uploads the document under the customer&apos;s card. Sticker
              goes on the hard copy and is handed to the customer.{" "}
              <strong>Postnet keeps nothing — zero admin.</strong>
            </Step>

            <Step n="03" title="Customer walks out" icon={CreditCard}>
              Walks out with the card and the hard copy (sticker on it).
              Same vault now lives <em>both</em> on paper and digitally
              inside the card. Either one verifies on any phone.
            </Step>

            <Step
              n="04"
              title="Verification — phone to phone"
              icon={DeviceMobile}
              color={POSTNET_RED}
            >
              When a fleet manager / Uber / employer needs to verify:
              customer opens their card on their phone, taps the document.
              Verifier opens their phone camera, points at the customer&apos;s
              screen. <strong>Pass / fail in under a second.</strong> No
              app, no install — just a browser camera.
            </Step>

            <Step
              n="05"
              title="90-day expiry"
              icon={ClockClockwise}
              color={POSTNET_RED}
            >
              Documents in the vault auto-stale after 90 days. The
              customer&apos;s card pings them. They walk back into Postnet to
              refresh. Another mint sticker. Same shelf, same clerk.
            </Step>
          </div>

          <div className="mt-10 bg-black text-white border-2 border-black p-6">
            <Eyebrow color="text-white/60">THE PRIVACY GUARANTEE</Eyebrow>
            <p className="mt-2 text-sm sm:text-base leading-relaxed text-neutral-200">
              Documents are <strong>never public</strong>. Only the
              cardholder can open the vault. Verifiers see the document only
              when the cardholder chooses to show them — on their own
              phone, in person. Postnet handles the certification.
              Curb Crew handles the encryption. The customer holds the key.
            </p>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 05 · UNIT ECONOMICS ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={5} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <div className="flex items-center gap-3">
            <Receipt size={20} weight="bold" style={{ color: POSTNET_RED }} />
            <Eyebrow>THE NUMBERS</Eyebrow>
          </div>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight mt-4">
            One price. Three SKUs.<br />
            <span style={{ color: POSTNET_RED }}>R49 wholesale, flat.</span>
          </h2>

          <div className="grid sm:grid-cols-2 gap-5 mt-12">
            <div className="border-2 border-black bg-neutral-50 p-6">
              <Eyebrow>WHAT WE SUPPLY · WHAT YOU PAY</Eyebrow>
              <table className="w-full text-sm mt-4">
                <tbody>
                  <tr className="border-b border-neutral-300">
                    <td className="py-2">Curb Crew Card · each</td>
                    <td className="text-right font-mono">R49</td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="py-2">Asset Sticker Pack · 10-pack</td>
                    <td className="text-right font-mono">R49</td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="py-2">Mint Sticker Pack · per pack</td>
                    <td className="text-right font-mono">R49</td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="py-2">Counter stand (pilot)</td>
                    <td className="text-right font-mono">FREE</td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="py-2">Operator portal access</td>
                    <td className="text-right font-mono">FREE</td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="py-2">Verification engine</td>
                    <td className="text-right font-mono">FREE</td>
                  </tr>
                  <tr className="border-b border-neutral-300">
                    <td className="py-2">Clerk training</td>
                    <td className="text-right font-mono">FREE</td>
                  </tr>
                  <tr className="font-bold" style={{ color: POSTNET_RED }}>
                    <td className="py-2">Per-unit wholesale</td>
                    <td className="text-right font-mono">R49</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-3 text-xs text-neutral-500">
                Restock on demand. No minimum order after the pilot kit.
                No subscription. No setup fee.
              </p>
            </div>

            <div
              className="border-2 border-black p-6 text-white"
              style={{ background: POSTNET_RED }}
            >
              <Eyebrow color="text-white/70">WHAT YOU CONTROL</Eyebrow>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed">
                <li>
                  <strong>Retail pricing.</strong> Your branch sets what the
                  customer pays. Curb Crew doesn&apos;t publish a recommended
                  retail price — that&apos;s your call.
                </li>
                <li>
                  <strong>Turnover reporting.</strong> All sales go through
                  your normal till. Your existing royalty and advertising
                  fees apply exactly as today — Curb Crew doesn&apos;t add or
                  change anything.
                </li>
                <li>
                  <strong>Customer relationship.</strong> The cardholder is
                  your customer. We don&apos;t market to them, we don&apos;t hold
                  their data, we don&apos;t cross-sell.
                </li>
                <li>
                  <strong>Stock level.</strong> Order what you need, when
                  you need it.
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 border-2 border-black overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  <th className="text-left p-3 font-mono text-[10px] tracking-[0.3em] uppercase">
                    PILOT STARTER KIT
                  </th>
                  <th className="text-right p-3 font-mono text-[10px] tracking-[0.3em] uppercase">
                    QTY
                  </th>
                  <th className="text-right p-3 font-mono text-[10px] tracking-[0.3em] uppercase">
                    WHOLESALE
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-neutral-200">
                  <td className="p-3 font-mono">Curb Crew Cards</td>
                  <td className="p-3 text-right font-mono">50</td>
                  <td className="p-3 text-right font-mono">R 2,450</td>
                </tr>
                <tr className="border-t border-neutral-200">
                  <td className="p-3 font-mono">Asset Sticker Packs</td>
                  <td className="p-3 text-right font-mono">20</td>
                  <td className="p-3 text-right font-mono">R 980</td>
                </tr>
                <tr className="border-t border-neutral-200">
                  <td className="p-3 font-mono">Mint Sticker Packs</td>
                  <td className="p-3 text-right font-mono">10</td>
                  <td className="p-3 text-right font-mono">R 490</td>
                </tr>
                <tr className="border-t border-neutral-200">
                  <td className="p-3 font-mono">Counter stand · training · portal</td>
                  <td className="p-3 text-right font-mono">1</td>
                  <td className="p-3 text-right font-mono">FREE</td>
                </tr>
                <tr className="border-t border-neutral-200 bg-neutral-100 font-bold">
                  <td className="p-3 font-mono">Total starter order</td>
                  <td className="p-3 text-right font-mono">—</td>
                  <td className="p-3 text-right font-mono" style={{ color: POSTNET_RED }}>
                    R 3,920
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-neutral-500">
            Suggested 60-day pilot allocation. Adjust quantities to match
            your branch foot-traffic.
          </p>

          <div className="mt-10 bg-black text-white border-2 border-black p-6 flex flex-col sm:flex-row gap-6 items-start">
            <Lock
              size={28}
              weight="bold"
              style={{ color: POSTNET_RED }}
              className="shrink-0"
            />
            <div>
              <Eyebrow color="text-white/60">FRANCHISE-FRIENDLY</Eyebrow>
              <p className="mt-2 text-sm sm:text-base leading-relaxed text-neutral-200">
                Any Postnet can refresh a customer&apos;s vault — there&apos;s no
                exclusivity. <strong>Convenience wins</strong>, not
                contract. Your branch wins because you sold the card, you
                trained the customer, and you&apos;re the closest one to them
                when the reminder pings.
              </p>
            </div>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 06 · THE PILOT ───────── */}
      <Slide bg="bg-black text-white" className="border-t-2 border-black">
        <SlideNum n={6} dark />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <div className="flex items-center gap-3">
            <Lightning
              size={20}
              weight="bold"
              style={{ color: POSTNET_RED }}
            />
            <Eyebrow color="text-white/60">THE PILOT</Eyebrow>
          </div>
          <h2 className="font-display text-5xl sm:text-7xl leading-tight mt-4">
            One branch.<br />
            <span
              className="px-2 inline-block"
              style={{ background: POSTNET_RED }}
            >
              60 days.
            </span>{" "}
            Zero risk.
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            <div className="bg-white text-black border-2 border-white p-6">
              <div className="font-display text-2xl">Week 1</div>
              <Eyebrow>SETUP</Eyebrow>
              <p className="text-sm mt-3 leading-relaxed">
                Starter kit ships (50 cards + 20 asset packs + 10 mint
                packs). Counter stand installs. 15-minute clerk training.
                Branded operator portal goes live at{" "}
                <code className="font-mono text-xs bg-neutral-100 px-1">
                  /cc/postnet
                </code>
                .
              </p>
            </div>
            <div className="bg-white text-black border-2 border-white p-6">
              <div className="font-display text-2xl">Week 2–8</div>
              <Eyebrow>LIVE SHELF</Eyebrow>
              <p className="text-sm mt-3 leading-relaxed">
                Stock on display. Drivers, students, parents add documents
                to vaults. Curb Crew handles fulfilment, dashboards, and
                support. Your branch handles retail.
              </p>
            </div>
            <div
              className="border-2 border-white p-6 text-white"
              style={{ background: POSTNET_RED }}
            >
              <div className="font-display text-2xl">Day 60</div>
              <Eyebrow color="text-white/80">REVIEW</Eyebrow>
              <p className="text-sm mt-3 leading-relaxed">
                Joint review: stock moved, vaults active, customers due for
                refresh, restock order placed (or not). If it&apos;s not
                working for you — we walk away. No contract, no lock-in.
              </p>
            </div>
          </div>

          <div className="mt-14 bg-white text-black border-2 border-white p-8">
            <Eyebrow>WHAT WE NEED FROM YOU</Eyebrow>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <strong>1.</strong> A1 of counter space for the Curb Crew stand.
              </li>
              <li>
                <strong>2.</strong> 15 minutes of one clerk&apos;s time to run
                through the portal.
              </li>
              <li>
                <strong>3.</strong> A starter order at R3,920 to get stock
                on the shelf.
              </li>
            </ul>
          </div>

          <div className="mt-10 bg-white text-black border-2 border-white p-8">
            <Eyebrow>NEXT STEP</Eyebrow>
            <div className="font-display text-2xl sm:text-3xl mt-3 leading-tight">
              15-minute call. I&apos;ll show you the live shelf on my phone.
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/cc/postnet"
                className="inline-flex items-center gap-2 text-white px-5 py-3 font-mono text-[11px] tracking-[0.3em] uppercase"
                style={{ background: POSTNET_RED }}
                data-testid="postnet-pitch-cta-live"
              >
                See the live issuer <ArrowRight size={14} weight="bold" />
              </Link>
              <a
                href="mailto:billy@hadlowinnovations.co.za?subject=Postnet%20%C3%97%20Curb%20Checkpoint%20pilot"
                className="inline-flex items-center gap-2 border-2 border-black px-5 py-3 font-mono text-[11px] tracking-[0.3em] uppercase"
                data-testid="postnet-pitch-cta-email"
              >
                Email Billy
              </a>
              <a
                href="https://wa.me/27?text=Postnet%20%C3%97%20Curb%20Checkpoint%20pilot%20%E2%80%94%20when%20can%20we%20chat%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 font-mono text-[11px] tracking-[0.3em] uppercase"
                data-testid="postnet-pitch-cta-whatsapp"
              >
                <WhatsappLogo size={14} weight="bold" /> WhatsApp Billy
              </a>
            </div>
          </div>

          <div className="mt-10 text-xs text-white/50 font-mono tracking-wider">
            Curb Checkpoint · A Curb Crew capability · curbcrew.co.za/pitch/postnet
          </div>
        </div>
      </Slide>
    </div>
  );
}

/* ─── small inline step block ─── */
function Step({ n, title, icon: Icon, color = "#000000", children }) {
  return (
    <div className="flex gap-5 items-start bg-white border-2 border-black p-5">
      <div
        className="w-12 h-12 flex items-center justify-center text-white shrink-0"
        style={{ background: color }}
      >
        <Icon size={20} weight="bold" />
      </div>
      <div className="flex-1">
        <div className="flex items-baseline gap-3">
          <div className="font-mono text-xs tracking-widest text-neutral-400">
            {n}
          </div>
          <div className="font-display text-xl leading-tight">{title}</div>
        </div>
        <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
          {children}
        </p>
      </div>
    </div>
  );
}
